// client/src/dnd/views/Interactive_parts/GridOverlay.js
import React, { useEffect, useRef, useState } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

export default function GridOverlay({ mapId, imageSize, showGrid, canEditGrid, onGridConfigChange, fogOpacityRef }) {
  const map = useMap();
  const layerRef = useRef();

  const [loading, setLoading] = useState(true);
  const [grid, setGrid] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [pending, setPending] = useState(false);
  const [settings, setSettings] = useState({
    cell_cols: 16,
    cell_rows: 10,
    color: "#ffffff",
    opacity: 0.25,
    offset_x: 0,
    offset_y: 0,
    square_length: 1.0,
    grid_shape: "square",
    start_x: 0,
    start_y: 0
  });

  useEffect(() => {
    if (!map) return;
    if (showModal) {
      map.dragging.disable();
      map.doubleClickZoom.disable();
      map.scrollWheelZoom.disable();
      map.boxZoom.disable();
      map.keyboard.disable();
      if (map.tap) map.tap.disable();
    } else {
      map.dragging.enable();
      map.doubleClickZoom.enable();
      map.scrollWheelZoom.enable();
      map.boxZoom.enable();
      map.keyboard.enable();
      if (map.tap) map.tap.enable();
    }
    return () => {
      if (!map) return;
      map.dragging.enable();
      map.doubleClickZoom.enable();
      map.scrollWheelZoom.enable();
      map.boxZoom.enable();
      map.keyboard.enable();
      if (map.tap) map.tap.enable();
    };
  }, [map, showModal]);

  useEffect(() => {
    if (!mapId) return;
    setLoading(true);
    fetch(`/api/tabletop/grids/${mapId}`)
      .then(res => res.json())
      .then(data => {
        const dbGrid = Array.isArray(data) && data.length > 0 ? data[0] : null;
        setGrid(dbGrid);
        setSettings(
          dbGrid
            ? {
                cell_cols: dbGrid.cell_cols,
                cell_rows: dbGrid.cell_rows,
                color: dbGrid.color,
                opacity: dbGrid.opacity,
                offset_x: dbGrid.offset_x,
                offset_y: dbGrid.offset_y,
                square_length: dbGrid.square_length,
                grid_shape: dbGrid.grid_shape,
                start_x: dbGrid.start_x,
                start_y: dbGrid.start_y
              }
            : {
                cell_cols: 16,
                cell_rows: 10,
                color: "#ffffff",
                opacity: 0.25,
                offset_x: 0,
                offset_y: 0,
                square_length: 1.0,
                grid_shape: "square",
                start_x: 0,
                start_y: 0
              }
        );
        setLoading(false);
      })
      .catch(() => {
        setGrid(null);
        setSettings({
          cell_cols: 16,
          cell_rows: 10,
          color: "#ffffff",
          opacity: 0.25,
          offset_x: 0,
          offset_y: 0,
          square_length: 1.0,
          grid_shape: "square",
          start_x: 0,
          start_y: 0
        });
        setLoading(false);
      });
  }, [mapId]);

  useEffect(() => {
    if (!imageSize) return;
    const [w, h] = imageSize;
    const cellSize = w / settings.cell_cols;
    const rowCount = Math.floor(h / cellSize);
    if (settings.cell_rows !== rowCount && rowCount > 0) {
      setSettings(g => ({ ...g, cell_rows: rowCount }));
    }
  }, [imageSize, settings.cell_cols, settings.cell_rows]);

  const lastSentConfigRef = useRef(null);

  useEffect(() => {
    if (loading) return;

    if (!map || !imageSize || !settings.cell_cols || settings.cell_cols < 2) {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
      if (onGridConfigChange) {
        lastSentConfigRef.current = null;
        onGridConfigChange(null);
      }
      return;
    }

    const [w, h] = imageSize;
    const cellSize = w / settings.cell_cols;
    const rowCount = Math.floor(h / cellSize);

    const metre_in_pixels = cellSize / settings.square_length;

    const newConfig = {
      cell_cols: settings.cell_cols,
      cell_rows: rowCount,
      cell_size: cellSize,
      color: settings.color,
      opacity: settings.opacity,
      offset_x: settings.offset_x,
      offset_y: settings.offset_y,
      square_length: settings.square_length,
      grid_shape: settings.grid_shape,
      start_x: settings.start_x,
      start_y: settings.start_y,
      metre_in_pixels: metre_in_pixels
    };

    if (
      onGridConfigChange &&
      JSON.stringify(lastSentConfigRef.current) !== JSON.stringify(newConfig)
    ) {
      lastSentConfigRef.current = newConfig;
      onGridConfigChange(newConfig);
    }

    if (!showGrid) {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
      return;
    }

    if (layerRef.current) {
      map.removeLayer(layerRef.current);
      layerRef.current = null;
    }

    const fogDarkness = fogOpacityRef ? clamp01(fogOpacityRef.current) : 0;
    const effectiveOpacity = settings.opacity * (1 - fogDarkness);

    const group = L.layerGroup();

    for (let i = 0; i <= settings.cell_cols; i++) {
      const x = i * cellSize + settings.offset_x;
      if (x >= 0 && x <= w) {
        group.addLayer(
          L.polyline(
            [
              [0, x],
              [h, x]
            ],
            {
              color: settings.color,
              opacity: effectiveOpacity,
              weight: 1,
              interactive: false,
              pane: "grid-pane"
            }
          )
        );
      }
    }

    for (let i = 0; i <= rowCount; i++) {
      const y = i * cellSize + settings.offset_y;
      if (y >= 0 && y <= h) {
        group.addLayer(
          L.polyline(
            [
              [y, 0],
              [y, w]
            ],
            {
              color: settings.color,
              opacity: effectiveOpacity,
              weight: 1,
              interactive: false,
              pane: "grid-pane"
            }
          )
        );
      }
    }

    group.addTo(map);
    layerRef.current = group;

    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [loading, showGrid, map, imageSize, settings, onGridConfigChange, fogOpacityRef]);

  async function handleSave() {
    setPending(true);
    try {
      const metre_in_pixels =
        imageSize && imageSize[0]
          ? (imageSize[0] / settings.cell_cols) / settings.square_length
          : 0;

      let res;
      if (grid && grid.id) {
        res = await fetch(`/api/tabletop/grids/${grid.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            cell_cols: settings.cell_cols,
            cell_rows: settings.cell_rows,
            color: settings.color,
            opacity: settings.opacity,
            offset_x: settings.offset_x,
            offset_y: settings.offset_y,
            square_length: settings.square_length,
            grid_shape: settings.grid_shape,
            start_x: settings.start_x,
            start_y: settings.start_y,
            metre_in_pixels: metre_in_pixels
          })
        });
      } else {
        res = await fetch(`/api/tabletop/grids`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            map_id: mapId,
            cell_cols: settings.cell_cols,
            cell_rows: settings.cell_rows,
            color: settings.color,
            opacity: settings.opacity,
            offset_x: settings.offset_x,
            offset_y: settings.offset_y,
            square_length: settings.square_length,
            grid_shape: settings.grid_shape,
            start_x: settings.start_x,
            start_y: settings.start_y,
            metre_in_pixels: metre_in_pixels
          })
        });
      }

      const json = await res.json();

      if (!res.ok || (json && json.error)) {
        console.error("Failed to save grid:", json && json.error);
        setPending(false);
        return;
      }

      const savedGrid = json;

      setGrid(savedGrid);
      setSettings({
        cell_cols: savedGrid.cell_cols,
        cell_rows: savedGrid.cell_rows,
        color: savedGrid.color,
        opacity: savedGrid.opacity,
        offset_x: savedGrid.offset_x,
        offset_y: savedGrid.offset_y,
        square_length: savedGrid.square_length,
        grid_shape: savedGrid.grid_shape,
        start_x: savedGrid.start_x,
        start_y: savedGrid.start_y
      });
      setShowModal(false);
    } catch (e) {
      console.error("Error saving grid:", e);
    }
    setPending(false);
  }

  async function handleDelete() {
    if (!grid || !grid.id) return;
    setPending(true);
    try {
      await fetch(`/api/tabletop/grids/${grid.id}`, { method: "DELETE" });
      setGrid(null);
      setSettings({
        cell_cols: 16,
        cell_rows: 10,
        color: "#ffffff",
        opacity: 0.25,
        offset_x: 0,
        offset_y: 0,
        square_length: 1.0,
        grid_shape: "square",
        start_x: 0,
        start_y: 0
      });
      if (onGridConfigChange) {
        lastSentConfigRef.current = null;
        onGridConfigChange(null);
      }
      setShowModal(false);
    } catch (e) {
      console.error("Error deleting grid:", e);
    }
    setPending(false);
  }

  if (loading) return null;

  return (
    <>
      {canEditGrid && (
        <div
          style={{
            position: "absolute",
            top: 16,
            left: 16,
            zIndex: 1650,
            display: "flex",
            gap: 10
          }}
        >
          <button
            style={{
              background: "#2c2c34",
              color: "#fff",
              border: "1px solid #888",
              borderRadius: 8,
              fontWeight: 500,
              fontSize: 14,
              padding: "4px 10px",
              height: 44,
              cursor: "pointer"
            }}
            onClick={() => setShowModal(true)}
          >
            Edit Grid
          </button>
        </div>
      )}
      {showModal && (
        <div
          style={{
            position: "fixed",
            left: "50%",
            top: "38%",
            transform: "translate(-50%, -50%)",
            background: "#23242a",
            padding: 22,
            borderRadius: 14,
            color: "#fff",
            zIndex: 2000,
            boxShadow: "0 4px 28px #000a",
            minWidth: 310
          }}
        >
          <div style={{ marginBottom: 12, fontWeight: 600, fontSize: 16 }}>Grid Settings</div>
          <div style={{ marginBottom: 18 }}>
            <label>
              Number of Columns (Squares):
              <br />
              <input
                type="range"
                min="16"
                max="50"
                value={settings.cell_cols}
                onChange={e =>
                  setSettings(g => ({
                    ...g,
                    cell_cols: Math.round(+e.target.value)
                  }))
                }
                style={{ width: 180, verticalAlign: "middle", marginRight: 10 }}
              />
              <span style={{ marginLeft: 8 }}>{settings.cell_cols}</span>
            </label>
          </div>
          <div style={{ marginBottom: 18 }}>
            <label>
              Square Size (meters):
              <br />
              <input
                type="range"
                min="1"
                max="50"
                step="0.5"
                value={settings.square_length}
                onChange={e =>
                  setSettings(g => ({
                    ...g,
                    square_length: parseFloat(e.target.value)
                  }))
                }
                style={{ width: 180, verticalAlign: "middle", marginRight: 10 }}
              />
              <span style={{ marginLeft: 8 }}>{settings.square_length.toFixed(1)}</span>
            </label>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label>
              Color:
              <br />
              <input
                type="color"
                value={settings.color}
                onChange={e => setSettings(g => ({ ...g, color: e.target.value }))}
                style={{ width: 48, height: 30, marginTop: 2, background: "none", border: "none" }}
              />
            </label>
          </div>
          <div style={{ marginBottom: 18 }}>
            <label>
              Opacity:
              <br />
              <input
                type="range"
                min="0.05"
                max="1"
                step="0.01"
                value={settings.opacity}
                onChange={e => setSettings(g => ({ ...g, opacity: +e.target.value }))}
                style={{ width: 120, marginLeft: 8, verticalAlign: "middle" }}
              />
              <span style={{ marginLeft: 8 }}>{Math.round(settings.opacity * 100)}%</span>
            </label>
          </div>
          <div
            style={{ marginBottom: 16, display: "flex", flexDirection: "column", alignItems: "center" }}
          >
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Adjust Grid Position</div>
            <div style={{ display: "grid", gridTemplateColumns: "32px 32px 32px", gap: 2 }}>
              <button
                onClick={() => setSettings(g => ({ ...g, offset_y: g.offset_y + 5 }))}
                style={{ gridColumn: "2", gridRow: "1", width: 32, height: 32, fontSize: 20 }}
              >
                ↑
              </button>
              <button
                onClick={() => setSettings(g => ({ ...g, offset_x: g.offset_x - 5 }))}
                style={{ gridColumn: "1", gridRow: "2", width: 32, height: 32, fontSize: 20 }}
              >
                ←
              </button>
              <button
                onClick={() => setSettings(g => ({ ...g, offset_x: g.offset_x + 5 }))}
                style={{ gridColumn: "3", gridRow: "2", width: 32, height: 32, fontSize: 20 }}
              >
                →
              </button>
              <button
                onClick={() => setSettings(g => ({ ...g, offset_y: g.offset_y - 5 }))}
                style={{ gridColumn: "2", gridRow: "3", width: 32, height: 32, fontSize: 20 }}
              >
                ↓
              </button>
            </div>
            <div style={{ marginTop: 10, fontSize: 13, color: "#bbb" }}>
              X offset: {settings.offset_x}, Y offset: {settings.offset_y}
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 14, marginTop: 10 }}>
            <button
              style={{
                background: "#4cc153",
                color: "#fff",
                border: "none",
                borderRadius: 7,
                padding: "4px 16px",
                fontWeight: 600,
                fontSize: 15,
                cursor: "pointer"
              }}
              onClick={handleSave}
              disabled={pending}
            >
              Save Grid
            </button>
            {!!grid && (
              <button
                style={{
                  background: "#d03e37",
                  color: "#fff",
                  border: "none",
                  borderRadius: 7,
                  padding: "4px 14px",
                  fontWeight: 600,
                  fontSize: 15,
                  cursor: "pointer"
                }}
                onClick={handleDelete}
                disabled={pending}
              >
                Delete
              </button>
            )}
            <button
              style={{
                background: "none",
                border: "1px solid #aaa",
                borderRadius: 7,
                color: "#fff",
                padding: "4px 16px",
                fontWeight: 600,
                fontSize: 15,
                cursor: "pointer"
              }}
              onClick={() => setShowModal(false)}
              disabled={pending}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function clamp01(v) {
  return Math.max(0, Math.min(1, v));
}
